import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { ParticipantPassesDataTable } from "@/components/participant-passes/participant-passes.data-table";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { participantPassesApi } from "@/services/participant-passes/participant-passes.api";
import { $fetch } from "@/lib/fetch";
import {
  type ParticipantPassDto,
  participantPassesDtoSchema,
} from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { type ParticipantPass } from "slde-api/db-schemas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ParticipantPassForm } from "@/components/participant-passes/participant-pass.form";

export default function ParticipantPassesPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [participantPassToUpdate, setParticipantPassToUpdate] =
    useState<ParticipantPass | null>(null);

  const {
    data: { items },
  } = useSuspenseQuery({
    queryKey: participantPassesApi.participantPasses.queryKey(),
    queryFn: async () =>
      await $fetch(participantPassesApi.participantPasses.route(), {
        output: participantPassesDtoSchema,
        throw: true,
      }),
  });

  const {
    mutate: createParticipantPass,
    isPending: isCreatingParticipantPass,
  } = useMutation({
    mutationFn: async (form: ParticipantPassDto) =>
      await $fetch(participantPassesApi.participantPasses.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Wejściówkę utworzono pomyślnie");
      setCreateDialogOpen(false);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu wejściówki: ${e.message}`);
      }
      console.error(e);
    },
  });

  const {
    mutate: updateParticipantPass,
    isPending: isUpdatingParticipantPass,
  } = useMutation({
    mutationFn: async ({ id, ...form }: ParticipantPass) =>
      await $fetch(participantPassesApi.participantPass.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano wejściówkę");
      setParticipantPassToUpdate(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił bład przy aktualizacji wejściówki: ${e.message}`);
      }
      console.error(e);
    },
  });

  const {
    mutate: deleteParticipantPass,
    isPending: isDeletingParticipantPass,
  } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(participantPassesApi.participantPass.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Wejściówka została usunięta");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu wejściówki: ${e.message}`);
      }
      console.error(e);
    },
  });

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">
            Wejściówki (PWZ / Legitymacje)
          </h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj wejściówkę
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <ParticipantPassesDataTable
          data={items}
          actions={{
            onUpdateClick: (participantPass) =>
              setParticipantPassToUpdate(participantPass),
            onDeleteClick: (id) => deleteParticipantPass({ id }),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!participantPassToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setParticipantPassToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!participantPassToUpdate
                ? "Edytuj wejściówkę"
                : "Dodaj wejściówkę"}
            </DialogTitle>
          </DialogHeader>

          <ParticipantPassForm
            onSubmit={(form) =>
              !!participantPassToUpdate
                ? updateParticipantPass({
                    id: participantPassToUpdate.id,
                    ...form,
                  })
                : createParticipantPass(form)
            }
            defaultValues={participantPassToUpdate}
            disabled={
              isCreatingParticipantPass ||
              isUpdatingParticipantPass ||
              isDeletingParticipantPass
            }
            loading={
              isCreatingParticipantPass ||
              isUpdatingParticipantPass ||
              isDeletingParticipantPass
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
